import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  Compass, Tag, QrCode, Bookmark, User,
  LayoutDashboard, Percent, UtensilsCrossed, BarChart3, Wallet, Store,
  Shield, LogOut, Menu, X, Bell, MessageSquare, MapPin, Megaphone, Pin
} from 'lucide-react';
import { api } from '@/utils/api';

const LS_BROADCAST_KEY = 'da_broadcast_seen_id';
const LS_SAVED_VISITED = 'da_saved_visited_ts';

/* ── URL-safe base64 → Uint8Array for VAPID applicationServerKey ── */
function urlBase64ToUint8Array(b64) {
  const pad = '='.repeat((4 - b64.length % 4) % 4);
  const raw = atob((b64 + pad).replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from([...raw].map(c => c.charCodeAt(0)));
}

/* ─── Broadcast Banner ────────────────────────────────────── */
export function BroadcastBanner() {
  const [broadcast, setBroadcast] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    api.get('/broadcasts/latest').then(r => {
      const b = r.data;
      if (!b || !b.id) return;
      // Pinned: only check sessionStorage (reappears every new browser visit)
      // Non-pinned: check localStorage (permanent dismissal)
      const store = b.is_pinned ? sessionStorage : localStorage;
      const seen = store.getItem(LS_BROADCAST_KEY);
      if (seen !== b.id) {
        setBroadcast(b);
        setVisible(true);
      }
    }).catch(() => {});
  }, []);

  const dismiss = () => {
    if (broadcast?.id) {
      // Pinned: session-only dismissal | Non-pinned: permanent
      const store = broadcast.is_pinned ? sessionStorage : localStorage;
      store.setItem(LS_BROADCAST_KEY, broadcast.id);
    }
    setVisible(false);
  };

  if (!visible || !broadcast) return null;

  const pinned = broadcast.is_pinned;
  return (
    <div className={`text-white px-4 py-2.5 flex items-center gap-3 z-40 ${
      pinned
        ? 'bg-amber-500'
        : 'bg-gradient-to-r from-orange-500 to-rose-500'
    }`}>
      {pinned
        ? <Pin className="w-4 h-4 flex-shrink-0" />
        : <Megaphone className="w-4 h-4 flex-shrink-0" />}
      <div className="flex-1 min-w-0">
        <span className="text-sm font-bold">{broadcast.title}</span>
        {broadcast.body && (
          <span className="text-sm text-white/90 ml-1.5 hidden sm:inline">{broadcast.body}</span>
        )}
        {pinned && <span className="ml-2 text-[10px] font-bold bg-white/25 px-1.5 py-0.5 rounded-full">PINNED</span>}
      </div>
      <button onClick={dismiss}
        data-testid="broadcast-dismiss-btn"
        className="flex-shrink-0 w-6 h-6 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-all">
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

// ─── Logo constants ───────────────────────────────────────────────────────────
const LOGO_TRANSPARENT = 'https://customer-assets-39nsmqrw.emergentagent.net/job_dining-portal-119/artifacts/5i4j17yj_ChatGPT_Image_Aug_31__2026__07_35_46_PM-removebg-preview.png';
const LOGO_WHITE_BG    = 'https://customer-assets-39nsmqrw.emergentagent.net/job_dining-portal-119/artifacts/c49clkor_Dine%20Around%20Logo.png';

const DineAroundLogo = ({ onDark = false, height = 'h-9' }) => (
  <img
    src={onDark ? LOGO_TRANSPARENT : LOGO_WHITE_BG}
    alt="Dine Around"
    className={`${height} w-auto object-contain`}
    style={onDark ? { filter: 'brightness(0) invert(1)' } : {}}
  />
);

const dinerNav = [
  { name: 'Discover', icon: Compass, path: '/diner/discover' },
  { name: 'Offers', icon: Tag, path: '/diner/offers' },
  { name: 'Scan QR', icon: QrCode, path: '/diner/scan' },
  { name: 'Saved', icon: Bookmark, path: '/diner/saved' },
  { name: 'Profile', icon: User, path: '/diner/profile' },
];

const vendorNav = [
  { name: 'Dashboard', icon: LayoutDashboard, path: '/vendor/dashboard' },
  { name: 'Offers', icon: Percent, path: '/vendor/offers' },
  { name: 'Menu', icon: UtensilsCrossed, path: '/vendor/menu' },
  { name: 'Analytics', icon: BarChart3, path: '/vendor/analytics' },
  { name: 'Wallet', icon: Wallet, path: '/vendor/wallet' },
  { name: 'Profile', icon: Store, path: '/vendor/profile' },
];

const adminNav = [
  { name: 'Overview', icon: LayoutDashboard, path: '/admin' },
  { name: 'Users', icon: User, path: '/admin/users' },
  { name: 'Restaurants', icon: Store, path: '/admin/restaurants' },
  { name: 'Reviews', icon: MessageSquare, path: '/admin/reviews' },
  { name: 'CMS', icon: Shield, path: '/admin/cms' },
];

/* ─── Diner notification bell ─────────────────────────── */
const LS_KEY = 'da_notif_read_ts';
const LS_PUSHED = 'da_notif_pushed_ids';

async function triggerPushNotification(notif) {
  if (Notification.permission !== 'granted') return;
  if (!('serviceWorker' in navigator)) return;
  try {
    const reg = await navigator.serviceWorker.ready;
    const pushedRaw = localStorage.getItem(LS_PUSHED) || '[]';
    const pushed = JSON.parse(pushedRaw);
    if (pushed.includes(notif.id)) return; // already shown
    reg.showNotification(
      `${notif.discount_value}% OFF — ${notif.restaurant_name || 'Nearby Restaurant'}`,
      {
        body: notif.title,
        icon: '/favicon.ico',
        badge: '/favicon.ico',
        tag: 'da-offer-' + notif.id,
        renotify: true,
        data: { url: '/diner/discover' },
      }
    );
    const updated = [...pushed.slice(-50), notif.id]; // keep last 50
    localStorage.setItem(LS_PUSHED, JSON.stringify(updated));
  } catch { /* silent — SW may not be active in this context */ }
}

function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [unread, setUnread] = useState(0);
  const [locLabel, setLocLabel] = useState('');
  const [radius, setRadius] = useState(null);
  const ref = useRef(null);

  /* Subscribe to VAPID push (idempotent — stores flag in localStorage) */
  const subscribeToPush = async () => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
    if (Notification.permission !== 'granted') return;
    if (localStorage.getItem('da_push_subscribed') === '1') return;
    try {
      const reg = await navigator.serviceWorker.ready;
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        const keyRes = await api.get('/push/vapid-public-key');
        const publicKey = keyRes.data?.publicKey;
        if (!publicKey) return;
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey),
        });
      }
      await api.post('/diner/push-subscribe', sub.toJSON());
      localStorage.setItem('da_push_subscribed', '1');
    } catch { /* silent — device may not support push */ }
  };

  useEffect(() => {
    // Load saved location label + request push permission + subscribe
    const setup = async () => {
      try {
        const r = await api.get('/diner/preferences');
        if (r.data.preferred_location_name) setLocLabel(r.data.preferred_location_name);
        if (r.data.preferred_radius) setRadius(r.data.preferred_radius);
      } catch {}

      if ('Notification' in window) {
        if (Notification.permission === 'default') {
          const perm = await Notification.requestPermission().catch(() => 'denied');
          if (perm === 'granted') await subscribeToPush();
        } else if (Notification.permission === 'granted') {
          await subscribeToPush();
        }
      }
    };
    setup();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const r = await api.get('/diner/notifications');
        const items = r.data || [];
        const lastRead = localStorage.getItem(LS_KEY) || '1970-01-01';
        const newItems = items.filter(n => (n.created_at || '') > lastRead);
        setNotifs(items);
        setUnread(newItems.length);
        // Fire push for any new items not yet pushed
        for (const n of newItems) {
          await triggerPushNotification(n);
        }
      } catch { }
    };
    load();
    const iv = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const locationLabel = locLabel
    ? (radius ? `${radius}km of ${locLabel}` : locLabel)
    : (radius ? `within ${radius}km` : null);

  const markAllRead = () => {
    localStorage.setItem(LS_KEY, new Date().toISOString());
    setUnread(0);
    setOpen(false);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="notification-bell"
        onClick={() => setOpen(!open)}
        className="relative w-8 h-8 rounded-full flex items-center justify-center hover:bg-white/20 transition-all">
        <Bell className="w-4.5 h-4.5 text-white w-[18px] h-[18px]" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center text-[9px] font-extrabold text-white">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-2xl shadow-2xl border border-purple-100 z-50 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <div>
              <h3 className="font-bold text-[#1E1B4B] text-sm">New Offers Nearby</h3>
              {locationLabel && (
                <p className="text-[10px] text-[#5B21B6] font-semibold mt-0.5 flex items-center gap-1">
                  <MapPin className="w-2.5 h-2.5" />{locationLabel}
                </p>
              )}
            </div>
            {unread > 0 && (
              <button onClick={markAllRead} className="text-xs text-[#5B21B6] font-semibold hover:opacity-80">
                Mark all read
              </button>
            )}
          </div>
          <div className="max-h-72 overflow-y-auto">
            {notifs.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <Bell className="w-7 h-7 mx-auto mb-2 opacity-30" />
                <p className="text-xs">No new high-discount offers in the last 24h</p>
              </div>
            ) : (
              notifs.slice(0, 8).map((n, i) => {
                const lastRead = localStorage.getItem(LS_KEY) || '1970-01-01';
                const isNew = (n.created_at || '') > lastRead;
                return (
                  <div key={n.id || i}
                    className={`flex items-center gap-3 px-4 py-3 border-b border-gray-50 last:border-0 ${isNew ? 'bg-purple-50/50' : ''}`}>
                    <div className="w-9 h-9 rounded-xl bg-purple-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
                      {n.restaurant_logo
                        ? <img src={n.restaurant_logo} alt="" className="w-full h-full object-cover" onError={e => { e.target.style.display='none'; }} />
                        : <Tag className="w-4 h-4 text-[#5B21B6]" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-[#1E1B4B] truncate">{n.title}</p>
                      <p className="text-[10px] text-gray-400 truncate">
                        {n.restaurant_name || ''} · <span className="text-[#5B21B6] font-bold">{n.discount_value}% OFF</span>
                      </p>
                      <p className="text-[10px] text-gray-300">
                        {n.created_at ? new Date(n.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                      </p>
                    </div>
                    {isNew && <span className="w-2 h-2 bg-[#5B21B6] rounded-full flex-shrink-0" />}
                  </div>
                );
              })
            )}
          </div>
          {notifs.length > 0 && (
            <div className="px-4 py-2.5 border-t border-gray-100 text-center">
              <p className="text-[10px] text-gray-400">Offers ≥ 25% discount · last 24 hours</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [savedBadge, setSavedBadge] = useState(0);

  /* Poll saved offers for diner role to compute new-offer badge */
  useEffect(() => {
    if (!user || user.role !== 'diner') return;
    const computeBadge = async () => {
      try {
        const r = await api.get('/diner/saved-offers');
        const offers = r.data || [];
        const lastVisit = localStorage.getItem(LS_SAVED_VISITED) || new Date().toISOString();
        const newCount = offers.filter(o => (o.created_at || '') > lastVisit).length;
        setSavedBadge(newCount);
        window.dispatchEvent(new CustomEvent('da_saved_badge', { detail: { count: newCount } }));
      } catch {}
    };
    computeBadge();
    const iv = setInterval(computeBadge, 5 * 60 * 1000);
    return () => clearInterval(iv);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (!user) return null;

  const navItems = user.role === 'vendor' ? vendorNav : user.role === 'admin' ? adminNav : dinerNav;
  const isActive = (path) => location.pathname === path || (path !== '/admin' && location.pathname.startsWith(path));
  const handleLogout = () => { logout(); navigate('/'); };

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50"
        style={{ background: 'linear-gradient(135deg, #3D0070 0%, #5B21B6 100%)' }}>
        <div className="max-w-7xl mx-auto px-4 h-14 flex items-center justify-between gap-3">
          <DineAroundLogo onDark height="h-8" />

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-0.5 flex-1 justify-center">
            {navItems.map(({ name, icon: Icon, path }) => {
              const isSaved = name === 'Saved' && user.role === 'diner';
              return (
                <button key={path} onClick={() => navigate(path)}
                  className={`relative flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                    isActive(path) ? 'bg-white/20 text-white' : 'text-white/65 hover:text-white hover:bg-white/10'
                  }`}>
                  <Icon className="w-4 h-4" />{name}
                  {isSaved && savedBadge > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-0.5 bg-red-500 rounded-full flex items-center justify-center text-[9px] font-extrabold text-white">
                      {savedBadge > 9 ? '9+' : savedBadge}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>

          {/* Right — notification bell (diner only) + avatar + logout */}
          <div className="flex items-center gap-2">
            {user.role === 'diner' && <NotificationBell />}
            <div className="hidden sm:flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center">
                <span className="text-white font-bold text-xs">{(user.name || 'U')[0].toUpperCase()}</span>
              </div>
              <span className="text-white/80 text-sm hidden lg:block truncate max-w-[120px]">{user.name}</span>
            </div>
            <button onClick={handleLogout}
              className="hidden sm:flex items-center gap-1 px-2 py-1 rounded-lg text-white/60 hover:text-white hover:bg-white/10 transition-all text-xs">
              <LogOut className="w-3.5 h-3.5" />
            </button>
            <button className="md:hidden text-white p-1" onClick={() => setMobileOpen(!mobileOpen)}>
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile drawer */}
        {mobileOpen && (
          <div className="md:hidden border-t border-white/10 px-4 py-3 space-y-1"
            style={{ background: 'rgba(61,0,112,0.97)' }}>
            {navItems.map(({ name, icon: Icon, path }) => (
              <button key={path} onClick={() => { navigate(path); setMobileOpen(false); }}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                  isActive(path) ? 'bg-white/20 text-white' : 'text-white/70 hover:bg-white/10 hover:text-white'
                }`}>
                <Icon className="w-4 h-4" />{name}
              </button>
            ))}
            <button onClick={handleLogout}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-red-300 hover:bg-red-500/10">
              <LogOut className="w-4 h-4" />Log out
            </button>
          </div>
        )}
      </header>
      <div className="h-14" />
    </>
  );
}
